/**
 * DraftingCanvas - the document itself: the canvas of the Drafting surface.
 *
 * One component for every state the body can be in, because they are all the
 * same region and swapping between them must not move anything:
 *
 *   a version preview      read-only markdown of an older snapshot
 *   a DOCX document        legacy matter documents, in a style-isolated iframe
 *   streaming markdown     the live document, with a quiet progress badge
 *   the collaborative editor   edit mode, Yjs-backed
 *   generation             an AI process - and the one place the logo loader
 *                          legitimately belongs
 *
 * THE CANVAS OWNS ITS SCROLL. Every view here is a `flex-1 min-h-0` child
 * that claims the height the shell already computed. The
 * `h-[calc(100vh-50px)]` that used to be on the markdown view and its
 * skeleton was a guess at the chrome above it - wrong in a pane, wrong once
 * the header wrapped, and the reason the page scrolled instead of the
 * document. See `shared/surface/README.md`, "The canvas height contract".
 */

import { memo, useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { PiEyeLight, PiSparkleLight } from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import CollaborativeDocumentEditor from '../../shared/CollaborativeDocumentEditor';
import { COLLAB_EDITING_ENABLED } from '../draftingConfig';
import MarkdownRenderer from '../../shared/MarkdownRenderer';
import { LoadingToCheckmark } from '../../shared/animations/LoadingToCheckmark';
import { Well } from '../../shared/brand/Field';
import { extractHtmlFromDocx } from '../../../utils/docxHandler';
import '../document.css';

export type DraftingStreamingState = 'idle' | 'streaming' | 'completed' | 'error';

export interface DraftingEditorUser {
  id: string;
  name: string;
  email: string;
}

/** The older snapshot being read instead of the live document. */
export interface DraftingPreview {
  versionId: string;
  content: string | null;
  loading: boolean;
}

export interface DraftingCanvasProps {
  status: string;
  streamingState: DraftingStreamingState;
  contentBytesReceived: number;
  currentContent: string | null;
  currentContentBlob: Blob | null;
  matterUuid: string;
  documentId: string;
  editorUser: DraftingEditorUser;
  readOnly: boolean;
  isProcessing: boolean;
  processingMessage: string | null;
  processingProgress: number | null;
  /** Non-null while a prior version is being previewed - it wins over all. */
  preview?: DraftingPreview | null;
  /** Persist an edit. Receives the editor's HTML (+ optional Tiptap JSON).
   *  May return a promise so the editor can show real save status. */
  onSave?: (html: string, json?: unknown, saveKind?: 'auto' | 'manual') => void | Promise<unknown>;
  /** Lossless Tiptap JSON to seed the editor with (preferred over markdown when
   *  present and fresh). */
  initialJson?: unknown;
}

// ─── Markdown → HTML converter ───────────────────────────────────────────────
/**
 * Renders streamed markdown to an HTML string for ProseMirror's initial content.
 * Uses the shared MarkdownRenderer so headings, lists, bold, tables, etc. all
 * survive the round-trip into the editor.
 */
function markdownToHtml(text: string): string {
  if (!text) return '';
  return renderToStaticMarkup(<MarkdownRenderer content={text} />);
}

// ─── Iframe HTML wrapper ──────────────────────────────────────────────────
function wrapHtmlForIframe(html: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const styleSheet = doc.querySelector('head style')?.textContent ?? '';
    const bodyHtml = doc.querySelector('body')?.innerHTML ?? html;
    const scrollbarCss = `
      html { scrollbar-width: thin; scrollbar-color: rgba(5,9,21,0.7) transparent; }
      html::-webkit-scrollbar { width: 8px; }
      html::-webkit-scrollbar-track { background: transparent; }
      html::-webkit-scrollbar-thumb { background: rgba(5,9,21,0.7); border-radius: 4px; }
    `;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${styleSheet}${scrollbarCss}</style></head><body>${bodyHtml}</body></html>`;
  } catch {
    return html;
  }
}

/**
 * The document COLUMN: a measure of text, not a card.
 *
 * It used to be a white sheet - rounded, hairlined, twice shadowed - floating
 * on the grey `bg-bg` canvas. That stacked white on white on grey: the sheet
 * inside a padded scroller inside an inset canvas. Now the CANVAS is the paper
 * (`canvasClassName` + `canvasBleed` on DetailPageSurface, see DraftingDetail),
 * and this is only the reading measure and the gutter around it.
 */
const DOC_PAGE = [
  /*
    Word's page, in CSS pixels at 96dpi: an 8.5in sheet (816px) with 1in
    margins (96px), giving the 6.5in measure every Letter/A4 legal document is
    set to. It was an 860px column with 64px gutters - a 732px measure, wider
    than the document will be when it is exported, so line breaks fell in
    different places here than in Word and long clauses ran past the ~90
    characters past which the eye loses the line's start.

    The gutters step down on narrow windows rather than the measure shrinking
    with them, so the text column stays the same shape on a laptop as on a
    phone.
  */
  'mx-auto w-full max-w-[816px]',
  'px-6 sm:px-12 lg:px-24 py-12 sm:py-16',
].join(' ');

/**
 * Contrast corrections for the shared MarkdownRenderer *on this page only*.
 * The renderer's defaults are tuned for the app canvas (gray-700 body); on the
 * white paper the body wants to be near-black, and on the dark canvas
 * (`bg-surface`) it wants to be lighter. The code/pre/thead grey blocks still
 * read against both. Scoped here so no shared component changes.
 */
const DOC_PROSE = [
  '[&_p]:text-gray-900 dark:[&_p]:text-gray-200',
  '[&_li]:text-gray-900 dark:[&_li]:text-gray-200',
  '[&_ul]:text-gray-900 dark:[&_ul]:text-gray-200',
  '[&_ol]:text-gray-900 dark:[&_ol]:text-gray-200',
  '[&_td]:text-gray-900 dark:[&_td]:text-gray-200',
  '[&_h1]:text-gray-950 [&_h2]:text-gray-950 [&_h3]:text-gray-950',
  '[&_h4]:text-gray-950 [&_h5]:text-gray-950 [&_h6]:text-gray-950',
  'dark:[&_h1]:text-white dark:[&_h2]:text-white dark:[&_h3]:text-white',
  'dark:[&_h4]:text-white dark:[&_h5]:text-white dark:[&_h6]:text-white',
  '[&_blockquote]:text-gray-700 dark:[&_blockquote]:text-gray-300',
  '[&_a]:text-info-700 dark:[&_a]:text-info-300',
  '[&_code]:bg-gray-100 dark:[&_code]:bg-gray-800',
  '[&_pre]:bg-gray-100 dark:[&_pre]:bg-gray-800',
  '[&_thead]:bg-gray-100 dark:[&_thead]:bg-gray-800',
].join(' ');

/**
 * The document's DEFAULT body font - Arial, not the app's UI font (Quicksand).
 * Set once on the page sheet so every descendant (headings, paragraphs, lists,
 * tables) inherits it; scoped to the sheet, so the surrounding panes, header and
 * app chrome keep the UI font. Code/pre keep their own `font-mono` class, which
 * wins over inheritance. When editing lands, the editor mounts inside the same
 * sheet and inherits this as its default - per-run font choices can override it.
 *
 * Inheritance alone is NOT enough: App.css styles `p, span, li` and `h1…h6` with
 * bare element selectors, which beat an inherited value. The real enforcement is
 * the `.pons-document` class below (see `../document.css`); this stays as the
 * column's own default and as the value the editor reads.
 */
export const DOC_FONT_FAMILY = 'Arial, Helvetica, sans-serif';

/** The class that carries Word's typography - see `../document.css`. */
export const DOC_FONT_CLASS = 'pons-document';

/** The document column: one width, one scroll owner, every view. */
const DocumentScroller: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
    <div className={`${DOC_FONT_CLASS} ${DOC_PAGE} ${DOC_PROSE}`} style={{ fontFamily: DOC_FONT_FAMILY }}>
      {children}
    </div>
  </div>
);

/** Read-only markdown view - no Yjs connection needed. */
// memo so re-renders that don't change `content` (e.g. the streaming overlay
// badge toggling off at stream end) don't re-parse the markdown - that re-parse
// on the final frame was part of the visible flicker.
const ReadOnlyMarkdownView = memo(function ReadOnlyMarkdownView(
  { content }: Readonly<{ content: string }>,
) {
  return (
    <DocumentScroller>
      <MarkdownRenderer content={content} />
    </DocumentScroller>
  );
});

/** Document-shaped placeholder while the content is being fetched/streamed.
 * The SAME scroller and column width as `ReadOnlyMarkdownView`, so the swap to
 * real content is seamless - no layout shift, no flash of a placeholder. */
export const DraftingDocSkeleton = memo(function DraftingDocSkeleton() {
  return (
    <DocumentScroller>
      <Skeleton variant="document" />
    </DocumentScroller>
  );
});

/** Read-only DOCX-derived HTML view (rendered in an iframe for style isolation). */
function ReadOnlyDocxView({ html }: Readonly<{ html: string }>) {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-h-0 flex justify-center overflow-hidden">
      <iframe
        title={t('Document viewer')}
        srcDoc={html}
        className="h-full w-full max-w-[816px] border-0 block"
      />
    </div>
  );
}

/** Converts a DOCX Blob to wrapped HTML once streaming completes. */
function useDocxHtml(currentContentBlob: Blob | null, streamingState: string) {
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [docxConverting, setDocxConverting] = useState(false);

  useEffect(() => {
    if (streamingState !== 'completed' || !currentContentBlob) return;
    let cancelled = false;
    setDocxConverting(true);
    (async () => {
      try {
        const arrayBuffer = await currentContentBlob.arrayBuffer();
        const html = await extractHtmlFromDocx(arrayBuffer);
        if (!cancelled) setDocxHtml(wrapHtmlForIframe(html));
      } catch {
        // Conversion failed - falls through to CollaborativeDocumentEditor
      } finally {
        if (!cancelled) setDocxConverting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentContentBlob, streamingState]);

  return { docxHtml, docxConverting };
}

type CompletedBodyProps = Readonly<
  Omit<DraftingCanvasProps, 'status' | 'isProcessing' | 'processingMessage' | 'processingProgress' | 'preview'>
>;

/** Rendered only when status === 'completed'. Isolated so hooks are at top level. */
function CompletedBody({
  streamingState,
  contentBytesReceived,
  currentContent,
  currentContentBlob,
  matterUuid,
  documentId,
  editorUser,
  readOnly,
  onSave,
  initialJson,
}: CompletedBodyProps) {
  const { t } = useTranslation();
  const { docxHtml, docxConverting } = useDocxHtml(currentContentBlob, streamingState);
  const kbLabel = contentBytesReceived > 0 ? ` - ${(contentBytesReceived / 1024).toFixed(1)} KB` : '';
  const spinnerMsg = docxConverting ? t('Preparing document…') : t('Loading preview…');
  const readOnlyReason = readOnly ? t('View mode — switch to Edit to make changes') : undefined;

  // Error takes over the whole canvas.
  if (streamingState === 'error') {
    return (
      <div className="flex flex-1 min-h-0 items-center justify-center p-6">
        <p className="text-sm text-danger-600 dark:text-danger-400">
          {t('Failed to load document content. Please try refreshing.')}
        </p>
      </div>
    );
  }

  const isStreaming = streamingState === 'streaming';
  const working = isStreaming || docxConverting;

  // DOCX (matter-mode) read-only → iframe, once converted.
  if (docxHtml && readOnly && !isStreaming) {
    return <ReadOnlyDocxView html={docxHtml} />;
  }

  // Markdown content lives in ONE stable container for BOTH the streaming and
  // completed phases. Keeping the same element type + position across the
  // stream→complete transition means React reconciles (just updates the
  // content prop) instead of unmounting/remounting the markdown view - the
  // remount was the flicker when the final document landed. The progress badge
  // is an overlay that simply disappears when streaming ends.
  if (currentContent && currentContent.length > 0 && (readOnly || isStreaming)) {
    return (
      <div className="relative flex flex-1 min-h-0 flex-col">
        <ReadOnlyMarkdownView content={currentContent} />
        {working && (
          <div className="pointer-events-none absolute top-2 end-4">
            <Badge color="sky" icon={<PiSparkleLight size={13} className="animate-fadePulse" />}>
              {spinnerMsg}{kbLabel}
            </Badge>
          </div>
        )}
      </div>
    );
  }

  // Awaiting content - covers the whole gap that used to flicker:
  //   • idle: the item (status='completed') arrived but the content stream
  //     hasn't opened yet (the seconds between the two API responses),
  //   • streaming: bytes are arriving but nothing renderable yet,
  //   • docxConverting: a DOCX blob is being turned into HTML.
  // Show a document-shaped skeleton in the SAME container as the real content so
  // the transition is a seamless swap - never a flash of "No preview available".
  const awaitingContent =
    !docxHtml &&
    !(currentContent && currentContent.length > 0) &&
    (streamingState === 'idle' || isStreaming || docxConverting);
  if (awaitingContent) {
    return <DraftingDocSkeleton />;
  }

  // Genuinely empty: the stream finished and produced nothing. Do NOT fall
  // through to the collaborative editor (that would open the Yjs socket).
  if (readOnly) {
    return (
      <div className="flex flex-1 min-h-0 items-center justify-center p-6">
        <p className="text-sm text-fg-muted">{t('No preview available yet.')}</p>
      </div>
    );
  }
  // Edit mode. PREFER the markdown source: it's already streamed to the client
  // and converts to HTML instantly, versus the DOCX→HTML path (mammoth in the
  // browser) which is slow for large agreements and was the original cause of
  // the blank-editor race + the long "Connecting…" wait. We persist markdown on
  // save anyway, so editing the markdown rendering is also the most consistent.
  const editorInitialHtml = (currentContent && currentContent.length > 0)
    ? markdownToHtml(currentContent)
    : (docxHtml ?? '');

  // GUARD (critical): if the document HAS source content but it has not yet
  // converted to editable HTML, do NOT open an empty editor - keep the skeleton
  // (or show an error if conversion failed). Opening empty here is what let an
  // autosave overwrite the whole draft with a blank/one-line document. The
  // editor only opens with the real content, or when the document is genuinely
  // empty (no blob and no markdown).
  const hasSourceContent = !!currentContentBlob || !!(currentContent && currentContent.length > 0);
  if (hasSourceContent && !editorInitialHtml) {
    return docxConverting ? (
      <DraftingDocSkeleton />
    ) : (
      <div className="flex flex-1 min-h-0 items-center justify-center p-6">
        <p className="text-sm text-danger-600 dark:text-danger-400">
          {t('Could not load the document for editing. Please refresh and try again.')}
        </p>
      </div>
    );
  }

  return (
    <div className={`${DOC_FONT_CLASS} flex flex-1 min-h-0 flex-col`}>
      <CollaborativeDocumentEditor
        matterUuid={matterUuid}
        documentId={documentId}
        initialHtml={editorInitialHtml}
        initialJson={initialJson}
        collabEnabled={COLLAB_EDITING_ENABLED}
        user={editorUser}
        readOnly={readOnly}
        readOnlyReason={readOnlyReason}
        placeholder={t('Start typing…')}
        autoSaveInterval={5000}
        onSave={onSave}
      />
    </div>
  );
}

/** A prior version, read in place of the live document. */
function PreviewBody({ preview }: Readonly<{ preview: DraftingPreview }>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="shrink-0 px-4 pt-3">
        <Well className="flex items-center gap-2 text-xs text-warning-800 dark:text-warning-200">
          <PiEyeLight size={14} />
          {t('Previewing an older version — read-only. Use Restore in the version history to make it current.')}
        </Well>
      </div>
      {preview.loading
        ? <DraftingDocSkeleton />
        : <ReadOnlyMarkdownView content={preview.content || ''} />}
    </div>
  );
}

/** Generation in flight - an AI process, and so the logo loader's own case. */
function GeneratingBody({
  processingMessage,
  processingProgress,
}: Readonly<Pick<DraftingCanvasProps, 'processingMessage' | 'processingProgress'>>) {
  const { t } = useTranslation();
  const pct = processingProgress !== null && processingProgress >= 0
    ? Math.min(100, Math.max(0, processingProgress))
    : null;
  const showBar = pct !== null && pct > 0 && pct < 100;

  return (
    <div className="flex flex-1 min-h-0 items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <LoadingToCheckmark isLoading={true} serviceName="Draft generation" />

        {/* Live step message from PubSub - falls back to the generic hint. */}
        <p className="mt-4 min-h-[1.25rem] animate-fade-in text-sm font-medium text-fg-body">
          {processingMessage || t('Generating your document…')}
        </p>

        {showBar && (
          <div className="mt-4 w-full max-w-xs">
            <div className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
              <div
                className="h-full rounded-full bg-champagne transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-fg-muted">{Math.round(pct)}%</p>
          </div>
        )}
      </div>
    </div>
  );
}

function DraftingCanvas(props: Readonly<DraftingCanvasProps>) {
  const { t } = useTranslation();
  const { status, isProcessing, processingMessage, processingProgress, preview, ...body } = props;

  if (preview) return <PreviewBody preview={preview} />;

  if (status === 'completed') return <CompletedBody {...body} />;

  if (status === 'failed') {
    return (
      <div className="flex flex-1 min-h-0 flex-col items-center justify-center p-6">
        <p className="text-sm text-danger-600 dark:text-danger-400">
          {t('Generation failed. Try generating again.')}
        </p>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <GeneratingBody
        processingMessage={processingMessage}
        processingProgress={processingProgress}
      />
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col items-center justify-center p-6">
      <PiSparkleLight size={48} className="mb-3 text-fg-muted" />
      <p className="text-sm text-fg-body">
        {t('No content yet. Click Generate to create the document.')}
      </p>
    </div>
  );
}

// Memoized: the editor surface is the heaviest child in the draft view, and its
// props from DraftingDetail are referentially stable (memoized editorUser/onSave,
// primitives, and a memoized preview object). Without this, unrelated parent
// state (renaming, dropdowns, pane toggles) re-rendered the whole editor.
export default memo(DraftingCanvas);
